import * as React from 'react';

import { useTheme } from '@veneer/theme';
import { css, SerializedStyles } from '@emotion/react';
import getPillBorder from '../utils/get_pill_border';
import { ButtonColorScheme } from './types';
import { doubleFocusRing } from '../utils/focus_ring';

type StyleKeys =
  | 'base'
  | 'disabledOrLoading'
  | 'disabledOrLoadingPrimary'
  | 'disabledOrLoadingSecondary'
  | 'disabledOrLoadingGhost'
  | 'icon'
  | 'label'
  | 'loading'
  | 'root'
  | 'rootActive'
  | 'rootSmall'
  | 'rootLeadingIconSmall'
  | 'rootTrailingIconSmall'
  | 'rootExpanded'
  | 'rootGhost'
  | 'rootGhostOverride'
  | 'rootGhostActive'
  | 'rootGhostActiveOverride'
  | 'rootGhostSmall'
  | 'rootGhostHover'
  | 'rootGhostHoverOverride'
  | 'rootGhostLeadingIcon'
  | 'rootGhostTrailingIcon'
  | 'rootGhostLeadingIconSmall'
  | 'rootGhostTrailingIconSmall'
  | 'rootIcon'
  | 'rootIconSmall'
  | 'rootLeadingIcon'
  | 'rootPrimary'
  | 'rootPrimaryActive'
  | 'rootPrimaryHover'
  | 'rootSecondary'
  | 'rootSecondaryOverride'
  | 'rootSecondaryActive'
  | 'rootSecondaryActiveOverride'
  | 'rootSecondaryHover'
  | 'rootSecondaryHoverOverride'
  | 'rootHc'
  | 'rootHoverHc'
  | 'rootActiveHc'
  | 'rootTrailingIcon'
  | 'spinner'
  | 'pill';

const useStyles = (
  buttonColorScheme: ButtonColorScheme
): Record<StyleKeys, SerializedStyles> => {
  const theme = useTheme();
  const colorScheme =
    buttonColorScheme === 'default' ? 'primary' : buttonColorScheme;

  return React.useMemo(() => {
    return {
      // Style to normalize button appearance on browsers
      base: css({
        appearance: 'none',
        backgroundColor: 'transparent',
        border: 0,
        margin: 0,
        boxSizing: 'border-box',
        overflow: 'visible',
        textDecoration: 'none',
        textTransform: 'none',
        userSelect: 'none',
        verticalAlign: 'middle',
        WebkitTapHighlightColor: 'transparent',
        whiteSpace: 'nowrap',
        outline: 0,
        boxShadow: 'none',
        flexShrink: 0,
        '&::-moz-focus-inner': {
          borderStyle: 'none',
        },
        ':hover': {
          textDecoration: 'none',
        },
      }),
      //root

      root: css({
        padding: `calc(${theme.orbitalIntermediate} - 1px) calc(${theme.orbitalFive} - 1px)`,
        position: 'relative',

        gap: theme.orbitalTwo,

        borderRadius: theme.borderRadius,
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'transparent',

        minWidth: '112px',

        alignItems: 'center',
        justifyContent: 'center',
        display: 'inline-flex',
        cursor: 'pointer',

        transition:
          'background-color 0.3s ease, border-color 0.3s ease, color 0.3s ease',

        '.vn-icon-active': {
          display: 'none',
        },

        '&:active': {
          '.vn-icon-active': {
            display: 'block',
          },

          '.vn-icon-inactive': {
            display: 'none',
          },
        },

        ...doubleFocusRing({
          theme,
          outlineOffset: -2,
          shadowSize: 3,
          shadowInset: true,
        }),
      }),

      rootActive: css({
        '.vn-icon-active': {
          display: 'block',
        },

        '.vn-icon-inactive': {
          display: 'none',
        },
      }),

      rootLeadingIcon: css({
        paddingLeft: `calc(${theme.orbitalFour} - 1px)`,
      }),

      rootTrailingIcon: css({
        paddingRight: `calc(${theme.orbitalFour} - 1px)`,
      }),

      rootLeadingIconSmall: css({
        paddingLeft: `calc(${theme.layout.size3} - 1px)`,
      }),

      rootTrailingIconSmall: css({
        paddingRight: `calc(${theme.layout.size3} - 1px)`,
      }),

      rootIcon: css({
        padding: `calc(${theme.orbitalIntermediate} - 1px)`,
        minWidth: 'unset',
      }),

      rootIconSmall: css({
        padding: '5px',
      }),

      rootExpanded: css({
        width: '100%',
      }),

      rootSmall: css({
        padding: `5px calc(${theme.layout.size4} - 1px)`,
        borderRadius: theme.borderRadius3,
        gap: theme.layout.size1,
      }),

      //primary

      rootPrimary: css({
        backgroundColor: theme.color[colorScheme].base.default,
        color: theme.color[colorScheme].foreground.default,
        '&:active': {
          backgroundColor: theme.color[colorScheme].base.active,
          color: theme.color[colorScheme].foreground.active,
        },
      }),

      rootPrimaryActive: css({
        backgroundColor: theme.color[colorScheme].base.active,
        color: theme.color[colorScheme].foreground.active,
      }),

      rootPrimaryHover: css({
        '&:hover': {
          backgroundColor: theme.color[colorScheme].base.hover,
          color: theme.color[colorScheme].foreground.hover,
        },
        '&:active': {
          backgroundColor: theme.color[colorScheme].base.active,
          color: theme.color[colorScheme].foreground.active,
        },
      }),

      disabledOrLoadingPrimary: css({
        backgroundColor: theme.color.background.disabled,
        '&:active': {
          color: 'transparent',
          borderColor: 'transparent',
          backgroundColor: theme.color.background.disabled,
        },
      }),

      //secondary

      rootSecondary: css({
        backgroundColor: 'transparent',
        borderStyle: 'solid',
        borderWidth: '1px',
        borderColor: theme.color[colorScheme].base.default,
        color: theme.color[colorScheme].base.default,
        '&:active': {
          backgroundColor: theme.color[colorScheme].background.active,
          borderColor: theme.color[colorScheme].base.active,
          color: theme.color[colorScheme].base.active,
        },
      }),

      rootSecondaryOverride: css({
        borderColor: theme.color[colorScheme].foreground.default,
        color: theme.color[colorScheme].foreground.default,
        '&:active': {
          backgroundColor: theme.color[colorScheme].background.active,
          borderColor: theme.color[colorScheme].foreground.active,
          color: theme.color[colorScheme].foreground.active,
        },
      }),

      rootSecondaryActive: css({
        backgroundColor: theme.color[colorScheme].background.active,
        borderColor: theme.color[colorScheme].base.active,
        color: theme.color[colorScheme].base.active,
      }),

      rootSecondaryActiveOverride: css({
        backgroundColor: theme.color[colorScheme].background.active,
        borderColor: theme.color[colorScheme].foreground.active,
        color: theme.color[colorScheme].foreground.active,
      }),

      rootSecondaryHover: css({
        '&:hover': {
          backgroundColor: theme.color[colorScheme].background.hover,
          borderColor: theme.color[colorScheme].base.hover,
          color: theme.color[colorScheme].base.hover,
        },
        '&:active': {
          backgroundColor: theme.color[colorScheme].background.active,
          borderColor: theme.color[colorScheme].base.active,
          color: theme.color[colorScheme].base.active,
        },
      }),

      rootSecondaryHoverOverride: css({
        '&:hover': {
          backgroundColor: theme.color[colorScheme].background.hover,
          borderColor: theme.color[colorScheme].foreground.hover,
          color: theme.color[colorScheme].foreground.hover,
        },
        '&:active': {
          backgroundColor: theme.color[colorScheme].background.active,
          borderColor: theme.color[colorScheme].foreground.active,
          color: theme.color[colorScheme].foreground.active,
        },
      }),

      rootHc: css({
        '&:active': {
          backgroundColor: theme.color[colorScheme].background.active,
          borderColor: theme.color[colorScheme].foreground.active,
          color: theme.color[colorScheme].foreground.active,
        },
      }),
      rootHoverHc: css({
        '&:hover': {
          backgroundColor: theme.color[colorScheme].background.hover,
          borderColor: theme.color[colorScheme].foreground.hover,
          color: theme.color[colorScheme].foreground.hover,
        },
        '&:active': {
          backgroundColor: theme.color[colorScheme].background.active,
          borderColor: theme.color[colorScheme].foreground.active,
          color: theme.color[colorScheme].foreground.active,
        },
      }),
      rootActiveHc: css({
        backgroundColor: theme.color[colorScheme].background.active,
        borderColor: theme.color[colorScheme].foreground.active,
        color: theme.color[colorScheme].foreground.active,
      }),

      disabledOrLoadingSecondary: css({
        borderColor: theme.color.foreground.disabled,
        '&:active': {
          color: 'transparent',
          borderColor: theme.color.foreground.disabled,
          backgroundColor: 'transparent',
        },
      }),

      //ghost

      rootGhost: css({
        padding: `calc(${theme.orbitalIntermediate} - 1px) calc(${theme.orbitalFour} - 1px)`,
        backgroundColor: 'transparent',
        color: theme.color[colorScheme].base.default,

        minWidth: '80px',

        '&:active': {
          backgroundColor: theme.color[colorScheme].background.active,
          color: theme.color[colorScheme].base.active,
        },
      }),

      rootGhostOverride: css({
        color: theme.color[colorScheme].foreground.default,
        '&:active': {
          backgroundColor: theme.color[colorScheme].background.active,
          color: theme.color[colorScheme].foreground.active,
        },
      }),

      rootGhostSmall: css({
        padding: `5px calc(${theme.layout.size3} - 1px)`,
      }),

      rootGhostActive: css({
        backgroundColor: theme.color[colorScheme].background.active,
        color: theme.color[colorScheme].base.active,
      }),

      rootGhostActiveOverride: css({
        backgroundColor: theme.color[colorScheme].background.active,
        color: theme.color[colorScheme].foreground.active,
      }),

      rootGhostHover: css({
        '&:hover': {
          backgroundColor: theme.color[colorScheme].background.hover,
          color: theme.color[colorScheme].base.hover,
        },
        '&:active': {
          backgroundColor: theme.color[colorScheme].background.active,
          color: theme.color[colorScheme].base.active,
        },
      }),

      rootGhostHoverOverride: css({
        '&:hover': {
          backgroundColor: theme.color[colorScheme].background.hover,
          color: theme.color[colorScheme].foreground.hover,
        },
        '&:active': {
          backgroundColor: theme.color[colorScheme].background.active,
          color: theme.color[colorScheme].foreground.active,
        },
      }),

      rootGhostLeadingIcon: css({
        paddingLeft: `calc(${theme.orbitalThree} - 1px)`,
      }),

      rootGhostTrailingIcon: css({
        paddingRight: `calc(${theme.orbitalThree} - 1px)`,
      }),

      rootGhostLeadingIconSmall: css({
        paddingLeft: `calc(${theme.layout.size2} - 1px)`,
      }),

      rootGhostTrailingIconSmall: css({
        paddingRight: `calc(${theme.layout.size2} - 1px)`,
      }),

      disabledOrLoadingGhost: css({
        color: theme.color.foreground.disabled,
        '&:active': {
          color: 'transparent',
          borderColor: 'transparent',
          backgroundColor: 'transparent',
        },
      }),

      disabledOrLoading: css({
        color: theme.color.foreground.disabled,
        cursor: 'default',
        pointerEvents: 'none',
      }),

      loading: css({
        color: 'transparent',
      }),

      label: css({
        textAlign: 'center',
        display: 'inherit',
        alignItems: 'center',
        ...theme.typography.labelRegular,
        lineHeight: '24px',
        [`@media (min-width: ${theme.layout.smMin})`]: {
          lineHeight: '24px',
        },
        [`@media (min-width: ${theme.layout.lgMin})`]: {
          lineHeight: '24px',
        },
      }),

      spinner: css({
        color: theme.color.foreground.disabled,
        display: 'inline-block',
        left: 'calc(50% - 12px)',
        position: 'absolute',
        top: 'calc(50% - 12px)',
        zIndex: 1,
      }),

      pill: css({
        borderRadius: getPillBorder,
      }),

      icon: css({
        display: 'inherit',
        '& > svg': {
          color: 'inherit',
          width: theme.layout.size6,
          height: theme.layout.size6,
        },
      }),
    };
  }, [theme, colorScheme]);
};

export default useStyles;
